<div class="flex flex-col h-full bg-white dark:bg-surface-2 transition-colors duration-300 relative">
  
  <!-- Sticky Header and Audio Player -->
  @if (!isChatView && !hideHeader) {
    <div class="sticky top-0 z-20 bg-white dark:bg-surface-2 shadow-sm border-b border-slate-100 dark:border-white/5 pb-1">
      <!-- Restructured Header (Top Row: URL + Agents, Bottom Row: Date) -->
      <div class="2xl:px-5 px-2 pt-5 pb-1 [@media(max-height:620px)]:pt-3 [@media(max-height:620px)]:pb-0">
        <div class="flex flex-col sm:flex-row sm:items-start justify-between mb-1 gap-2 sm:gap-4">
          <h1 class="text-[18px] sm:text-[20px] font-bold text-[#141437] dark:text-white leading-tight tracking-tight max-w-full sm:max-w-[70%] truncate">
            {{ meetingUrl || 'Meeting Recording' }}
          </h1>
          @if (agents) {
            <div class="text-right pt-0.5">
              <span class="text-[13px] text-slate-400 dark:text-slate-500 font-medium whitespace-nowrap">
                Agents: <span class="text-[#141437] dark:text-white font-bold">{{ agents }}</span>
              </span>
            </div>
          }
        </div>
        <div class="flex items-center gap-2 text-slate-400 dark:text-slate-500 text-[13px] font-medium px-1">
          <span>{{ dateTime }}</span>
        </div>
      </div>
      
      <!-- Horizontal Audio Player (Compact) -->
      <div class="mx-5 my-3 px-4 py-2.5 bg-slate-50/80 dark:bg-white/5 rounded-xl flex items-center gap-4 group border border-slate-100 dark:border-white/5 shadow-sm [@media(max-height:620px)]:my-2 [@media(max-height:620px)]:py-1.5">
        <span class="text-[13px] font-bold text-[#141437] dark:text-white whitespace-nowrap">Recording</span>
        
        <button (click)="togglePlay.emit()" 
                class="w-8 h-8 shrink-0 rounded-full bg-[#A446FF] text-white flex items-center justify-center hover:bg-[#9235FF] transition-all shadow-lg active:scale-95">
          <app-icon-v2 [name]="isPlaying ? 'pause' : 'play'" [size]="14" [class.ml-0.5]="!isPlaying"></app-icon-v2>
        </button>

        <div class="flex-1 h-5 flex items-center relative group/track cursor-pointer" (click)="onSeek($event)">
          <div class="flex-1 h-1.5 bg-purple-100 dark:bg-purple-900/20 rounded-full overflow-hidden relative">
            <div class="absolute left-0 top-0 h-full bg-[#A446FF] rounded-full"
                 [style.width.%]="(currentTime / audioDuration) * 100 || 0">
            </div>
          </div>
          <!-- Progress Circle (Unfilled) -->
          <div class="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-[#A446FF] bg-white dark:bg-slate-900 rounded-full -ml-1 transition-all duration-300 z-10 shadow-sm opacity-0 group-hover/track:opacity-100"
               [style.left.%]="(currentTime / audioDuration) * 100 || 0">
          </div>
        </div>

        <div class="text-[13px] font-bold text-[#141437] dark:text-white tabular-nums whitespace-nowrap">
          {{ formatTime(currentTime) }} / {{ formatTime(audioDuration) }}
        </div>

        <button (click)="toggleMute.emit()" class="flex text-slate-400 dark:text-slate-500 hover:text-[#A446FF] transition-colors">
          <app-icon-v2 [name]="isMuted ? 'volumeX' : 'volume2'" [size]="16"></app-icon-v2>
        </button>
      </div>
    </div>
  }

  <!-- Transcription Content (Compact) -->
  <div class="flex-1 min-h-0 flex flex-col relative" [ngClass]="{'dark:bg-surface-1': isChatView}">
    @if (!isChatView && !hideHeader) {
      <div class="px-5 py-3 flex items-center justify-between border-b border-slate-100 dark:border-white/5 bg-white dark:bg-surface-2 z-10 sticky top-0 [@media(max-height:620px)]:py-2">
        <h2 class="text-[14px] sm:text-[15px] font-bold text-[#141437] dark:text-white">Transcription</h2>
        <button (click)="downloadTranscript()" class="flex items-center gap-1.5 text-[#A446FF] hover:text-[#9235FF] font-bold transition-all group text-[14px] cursor-pointer">
          <app-icon-v2 name="download" [size]="14" class="group-hover:-translate-y-0.5 transition-transform"></app-icon-v2>
          Download
        </button>
      </div>
    }
    <div #scrollContainer (scroll)="handleScroll($event)" (wheel)="manualScroll()" (mousedown)="manualScroll()"
      (touchstart)="manualScroll()" class="flex-1 overflow-y-auto scrollbar-hide scroll-smooth relative"
      [ngClass]="isChatView ? 'p-4 space-y-4' : 'px-5 py-5 space-y-5'">
      
      @if (isChatView && (!messages || messages.length === 0)) {
        <div class="flex flex-col items-center justify-center h-full min-h-[300px] py-12 text-slate-500 dark:text-slate-400">
          <app-icon-v2 name="Inbox" size="64" customClass="text-slate-300 dark:text-slate-600 mb-4"></app-icon-v2>
          <p class="text-[16px] font-bold text-[#141437] dark:text-white">No Transcription Available</p>
          <p class="text-[14px] mt-2 text-slate-400 dark:text-slate-500 text-center max-w-sm leading-relaxed">There is currently no transcribed data or conversation history associated with this session.</p>
        </div>
      }

      @for (msg of messages; track $index) {
      <div
        class="transcript-wrapper flex w-full animate-in fade-in slide-in-from-bottom-4 duration-500 transition-all duration-300"
        [ngClass]="{
                   'justify-start': !isChatView && msg.role === 'CUSTOMER',
                   'justify-end': !isChatView && msg.role === 'AGENT',
                   'justify-center w-full': !isChatView && msg.role === 'AI',
                   'opacity-70 grayscale-[0.05]': !isChatView && activeTranscriptIndex !== -1 && activeTranscriptIndex !== $index,
                   'opacity-100 scale-[1.01]': !isChatView && activeTranscriptIndex === $index
                 }">
    
        <!-- AI Message Card -->
        @if (msg.role === 'AI') {
        @if (isChatView) {
        <!-- Chat View AI style -->
        <div class="flex items-start gap-3 w-full">
          <div
            class="w-10 h-10 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center flex-shrink-0 shadow-sm transition-transform hover:scale-105">
            <app-icon-v2 name="bot" class="text-white w-6 h-6" />
          </div>
          <div class="flex-1 max-w-[85%] border-transparent">
            <div class="flex items-center gap-2 mb-1">
              <span class="text-[14px] font-semibold text-[#4F46E5] dark:text-purple-400">{{ msg.sender }}</span>
              <span class="text-[10px] text-gray-400 font-medium ml-1">{{ msg.formattedTime || msg.time }}</span>
            </div>
            <div
              class="bg-white dark:bg-surface-2 rounded-2xl rounded-tl-none px-4 py-3 shadow-sm border border-slate-100 dark:border-slate-700 transition-all hover:shadow-md markdown-content">
              <markdown class="text-[14px] text-[#1E1B4B] dark:text-white leading-relaxed"
                [data]="formatMessage(msg.content)"></markdown>
            </div>
          </div>
        </div>
        } @else {
        <!-- Transcription AI style -->
         <!-- [class.border-[#A446FF]]="activeTranscriptIndex === $index" 
          [class.shadow-[0_0_15px_rgba(164,70,255,0.15)]]="activeTranscriptIndex === $index"-->
        <div
          class="w-full bg-pink-50/50 dark:bg-pink-950/10 border-2 border-purple-400/40 rounded-2xl overflow-hidden p-4 shadow-sm transition-all duration-300 hover:shadow-md markdown-content">
          <div class="flex items-center gap-2.5 mb-2">
            <div
              class="w-8 h-8 rounded-full bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center flex-shrink-0 shadow-sm transition-transform hover:scale-105">
              <app-icon-v2 name="bot" class="text-white w-5 h-5" />
            </div>
            <div class="flex items-center gap-2">
              <span class="text-sm font-semibold text-[#4F46E5] dark:text-purple-400">{{ msg.sender }}</span>
              <span class="text-[10px] text-gray-400 font-medium ml-1">[{{ msg.formattedTime || '0:00' }}]</span>
            </div>
          </div>
          <markdown class="text-sm text-[#1E1B4B] dark:text-white leading-relaxed" [data]="formatMessage(msg.content)">
          </markdown>
        </div>
        }
        } @else {
        @if (isChatView) {
        <!-- Chat View User style -->
        <div class="flex items-start gap-3 w-full" [ngClass]="{ 'flex-row-reverse': msg.role === 'AGENT' }">
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm transition-transform hover:scale-105"
            [ngClass]="msg.role === 'AGENT' ? 'bg-[#141437] dark:bg-slate-700' : 'bg-blue-600'">
            @if (msg.role === 'AGENT') {
              <app-icon-v2 name="user" class="text-white w-6 h-6" />
            } @else {
              <span class="text-white font-bold text-sm">{{ getInitials(msg.sender) }}</span>
            }
          </div>
          <!-- Message Area -->
          <div class="flex flex-col max-w-[85%]" [ngClass]="{ 'items-end': msg.role === 'AGENT', 'items-start': msg.role === 'CUSTOMER' }">
            <div class="flex items-center gap-2 mb-1" [ngClass]="{ 'flex-row-reverse': msg.role === 'AGENT' }">
              <span class="text-[14px] font-semibold text-gray-700 dark:text-gray-300">{{ msg.sender }}</span>
              <span class="text-[10px] font-medium text-slate-400 dark:text-slate-500">{{ msg.formattedTime || msg.time }}</span>
            </div>
    
            <div
              class="rounded-2xl px-4 py-3 shadow-sm border transition-all hover:shadow-md"
              [ngClass]="{
                'bg-slate-50 dark:bg-surface-2 rounded-tr-none border-slate-100 dark:border-slate-700': msg.role === 'AGENT',
                'bg-white dark:bg-slate-800 rounded-tl-none border-slate-200 dark:border-slate-700': msg.role === 'CUSTOMER'
              }">
              <div class="text-[14px] text-gray-800 dark:text-gray-200 leading-relaxed whitespace-pre-wrap"
                [innerHTML]="msg.content"></div>
            </div>
          </div>
        </div>
        } @else {
        <!-- Transcription User style -->
        <div class="flex gap-2 sm:gap-3 max-w-[90%] sm:max-w-[80%]" [ngClass]="{ 'flex-row-reverse': msg.role === 'AGENT' }">
    
                <!-- Standard Avatar -->
                <div class="shrink-0 pt-0.5" [ngClass]="{'hidden sm:block': true}">
                  <div class="w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white font-bold text-[11px] sm:text-[12px] shadow-sm"
                    [ngClass]="msg.role === 'AGENT' ? 'bg-[#141437]' : 'bg-blue-600'">
                    @if (msg.role === 'AGENT') {
                      <app-icon-v2 name="user" class="text-white w-5 h-5 flex shrink-0" />
                    } @else {
                      {{ getInitials(msg.sender) }}
                    }
                  </div>
                </div>
    
          <!-- Message Area -->
          <div class="flex flex-col"
            [ngClass]="{ 'items-end': msg.role === 'AGENT', 'items-start': msg.role === 'CUSTOMER' }">
            <div class="flex items-center gap-1.5 mb-1 px-1" [ngClass]="{ 'flex-row-reverse': msg.role === 'AGENT' }">
              <span class="text-[14px] font-bold text-[#141437] dark:text-white">{{ msg.sender }}</span>
              <span class="text-[12px] font-medium text-slate-400 dark:text-slate-500">[{{ msg.formattedTime || '0:00'
                }}]</span>
            </div>
    
            <div class="p-3 sm:p-4 rounded-2xl transition-all duration-300" [ngClass]="{
                             'bg-slate-50/80 dark:bg-white/5 text-[#141437] dark:text-slate-200 border border-slate-100 dark:border-white/5': msg.role === 'CUSTOMER',
                             'bg-indigo-50/50 dark:bg-indigo-950/20 text-[#141437] dark:text-slate-200': msg.role === 'AGENT'
                           }">
              <div class="text-[14px] sm:text-[15px] leading-relaxed whitespace-pre-wrap" [innerHTML]="msg.content"></div>
            </div>
          </div>
        </div>
        }
        }
      </div>
      }
    </div>

  <!-- Floating Resume Button -->
  @if (showResumeScrollButton()) {
    <div class="sticky bottom-8 h-0 z-30 flex justify-center pointer-events-none">
      <button (click)="resumeAutoScroll()"
              class="pointer-events-auto px-5 py-2.5 bg-[#A446FF] hover:bg-[#9235FF] text-white text-[13px] font-bold rounded-full shadow-[0_8px_20px_rgba(164,70,255,0.3)] flex items-center gap-2 group transition-all transform hover:-translate-y-0.5 active:scale-95 cursor-pointer">
        <app-icon-v2 [name]="scrollDirection() === 'up' ? 'ArrowUp' : 'ArrowDown'" [size]="16" class="group-hover:animate-bounce"></app-icon-v2>
        {{ resumeScrollLabel() }}
      </button>
    </div>
  }
</div>
